import { networkStatusSchema, type NetworkStatus } from "@woven/contracts";
import { useCallback, useEffect, useState } from "react";
import { ControlApiError } from "../../lib/api";
import { controlApiBase } from "../../lib/env";
import { isTerminalPollingError, startSequentialPolling } from "../../lib/polling";

const TELEMETRY_POLL_INTERVAL_MS = 20_000;
const TELEMETRY_REQUEST_TIMEOUT_MS = 15_000;

async function loadTelemetry<T>(load: (signal: AbortSignal) => Promise<T>, signal: AbortSignal) {
  signal.throwIfAborted();
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abort: (() => void) | undefined;
  try {
    return await new Promise<T>((resolve, reject) => {
      abort = () => {
        clearTimeout(timer);
        controller.abort(signal.reason);
        reject(signal.reason);
      };
      signal.addEventListener("abort", abort, { once: true });
      timer = setTimeout(() => {
        const error = new Error("Metrics request timed out.");
        controller.abort(error);
        reject(error);
      }, TELEMETRY_REQUEST_TIMEOUT_MS);
      load(controller.signal).then(resolve, reject);
    });
  } finally {
    clearTimeout(timer);
    if (abort) signal.removeEventListener("abort", abort);
  }
}

async function fetchPublicNetworkStatus(signal?: AbortSignal): Promise<NetworkStatus> {
  const response = await fetch(`${controlApiBase(import.meta.env)}/v1/network/status`, {
    signal,
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new ControlApiError(response.status, "UNAVAILABLE", "Telemetry unavailable");
  }
  return networkStatusSchema.parse(await response.json());
}

export function useTelemetry<T extends NetworkStatus>(load: (signal: AbortSignal) => Promise<T>) {
  const [snapshot, setSnapshot] = useState<{
    data: T | null;
    samples: number[];
    lastObservedAt: string | null;
  }>({ data: null, samples: [], lastObservedAt: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingStopped, setPollingStopped] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    setPollingStopped(false);
    setRefreshVersion((version) => version + 1);
  }, []);
  useEffect(
    () =>
      startSequentialPolling({
        intervalMs: TELEMETRY_POLL_INTERVAL_MS,
        load: (signal) => {
          setLoading(true);
          return loadTelemetry(load, signal);
        },
        onSuccess: (next) => {
          setLoading(false);
          setError(null);
          setPollingStopped(false);
          setSnapshot((previous) => {
            if (next.freshness !== "fresh" || next.connectionsActive === null || !next.observedAt) {
              return { data: next, samples: [], lastObservedAt: null };
            }
            const samples =
              next.observedAt === previous.lastObservedAt
                ? previous.samples
                : [...previous.samples, next.connectionsActive].slice(-60);
            return { data: next, samples, lastObservedAt: next.observedAt };
          });
        },
        onError: (failure) => {
          setLoading(false);
          setError(failure instanceof Error ? failure.message : "Telemetry unavailable");
          setPollingStopped(isTerminalPollingError(failure));
          setSnapshot({ data: null, samples: [], lastObservedAt: null });
        },
      }),
    [load, refreshVersion]
  );
  return { data: snapshot.data, samples: snapshot.samples, reload, loading, error, pollingStopped };
}

export function usePublicNetworkTelemetry() {
  return useTelemetry(fetchPublicNetworkStatus);
}
